import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  GaugeIcon,
  TagIcon,
  UsersThreeIcon,
  MegaphoneIcon,
  GiftIcon,
  CalendarCheckIcon,
  GearIcon,
  BellRingingIcon,
  ShippingContainerIcon,
  XIcon,
  PlugsIcon,
  QuestionIcon,
  SignOutIcon,
  KeyIcon,
  CoinsIcon,
  RobotIcon,
} from "@phosphor-icons/react";

const navItems = [
  { to: "/", label: "Dashboard", icon: GaugeIcon, testId: "nav-dashboard" },
  { to: "/keywords", label: "Keywords", icon: TagIcon, testId: "nav-keywords" },
  { to: "/groups", label: "Groups", icon: UsersThreeIcon, testId: "nav-groups" },
  { to: "/broadcast", label: "Broadcast", icon: MegaphoneIcon, testId: "nav-broadcast" },
  { to: "/holidays", label: "Holidays", icon: GiftIcon, testId: "nav-holidays" },
  { to: "/campaigns", label: "Campaigns", icon: CalendarCheckIcon, testId: "nav-campaigns" },
  { to: "/ai-budget", label: "AI Budget", icon: CoinsIcon, testId: "nav-ai-budget" },
  { to: "/grok-bot", label: "Grok Bot", icon: RobotIcon, testId: "nav-grok-bot" },
  { to: "/notifications", label: "Alerts log", icon: BellRingingIcon, testId: "nav-notifications" },
  { to: "/connections", label: "Connections", icon: PlugsIcon, testId: "nav-connections" },
  { to: "/settings", label: "Settings", icon: GearIcon, testId: "nav-settings" },
  { to: "/help", label: "Help & setup", icon: QuestionIcon, testId: "nav-help" },
];

export default function Sidebar({ open, onClose }) {
  const { user, appMode, logout } = useAuth();
  const navigate = useNavigate();
  const items = navItems.filter((n) => !n.corporateOnly || appMode === "corporate");
  return (
    <>
      {/* Mobile backdrop */}
      <div
        data-testid="sidebar-backdrop"
        onClick={onClose}
        className={`lg:hidden fixed inset-0 bg-zinc-950/40 z-30 transition-opacity ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />
      <aside
        className={`w-64 fixed left-0 top-0 h-screen border-r border-zinc-200 bg-white flex flex-col z-40 transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
        data-testid="sidebar"
      >
        <div className="px-6 py-6 border-b border-zinc-200 flex items-center gap-3">
          <div className="w-9 h-9 bg-zinc-950 text-white flex items-center justify-center">
            <ShippingContainerIcon size={22} weight="bold" />
          </div>
          <div className="flex-1">
            <div className="font-heading font-black text-base leading-none tracking-tight">
              CARGO/SCAN
            </div>
            <div className="overline mt-1">v1 · Personal</div>
          </div>
          <button
            onClick={onClose}
            data-testid="sidebar-close"
            className="lg:hidden p-1 text-zinc-500 hover:text-zinc-950"
            aria-label="Close menu"
          >
            <XIcon size={20} weight="bold" />
          </button>
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          {navItems.map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testId}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm transition-colors border-l-2 ${
                  isActive
                    ? "border-zinc-950 bg-zinc-50 text-zinc-950 font-medium"
                    : "border-transparent text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900"
                }`
              }
            >
              <Icon size={18} weight="bold" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-zinc-200 p-4">
          <div className="overline mb-2">Mode · {appMode}</div>
          {user ? (
            <div className="text-xs text-zinc-700 mb-2">
              <div className="font-medium truncate">{user.email}</div>
              <div className="text-zinc-500 uppercase">{user.role}</div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs mb-2">
              <span className="w-1.5 h-1.5 bg-emerald-500 live-dot" />
              <span className="text-zinc-700">Backend live</span>
            </div>
          )}
          {user ? (
            <>
              <button
                data-testid="change-password-btn"
                onClick={() => { onClose(); navigate("/change-password"); }}
                className="flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-950 mb-2"
              >
                <KeyIcon size={14} /> Change password
              </button>
              <button
                data-testid="logout-btn"
                onClick={() => { logout(); onClose(); navigate("/login"); }}
                className="flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-950 mb-2"
              >
                <SignOutIcon size={14} /> Sign out
              </button>
            </>
          ) : null}
          <div className="text-[10px] text-zinc-400 leading-relaxed">
            Run the WhatsApp listener on your mini PC. See Help.
          </div>
        </div>
      </aside>
    </>
  );
}
